<template>
  <div :class="wrapperClasses">
    <div class="dots__values">
      <div class="dots__values-text"></div>
      <div class="dots__values-value"></div>
    </div>
    <div :class="dotClasses(1)" ref="positiveDot">
      <div class="dots__plus"></div>
    </div>
    <div :class="dotClasses(2)"></div>
    <div :class="dotClasses(3)"></div>
    <div :class="dotClasses(4)"></div>
    <div :class="dotClasses(5)"></div>
    <div :class="dotClasses(6)"></div>
    <div :class="dotClasses(7)" ref="negativeDot">
      <div class="dots__minus"></div>
    </div>
    <div :class="balanceClasses">
      <div class="dots__balance-title">{{ title }}</div>
      <div data-cy="balance-value" class="dots__balance-value">
        <Wattage :value="animatedBalance" />
      </div>
    </div>
  </div>
</template>

<script>
  import Wattage from '@/components/Wattage.vue';
  import { useStrings } from '@/composables/useStrings.ts';
  import { DOM_RECT_EMPTY, WATTAGE_TWEEN_DURATION } from '@/constants';
  import TweenLite from 'gsap';
  import { useTemplateRef } from 'vue';
  import { useCurrentReadsStore } from '@/stores/useCurrentReadsStore';

  export default {
    name: 'dots',
    components: {
      Wattage,
    },
    setup(){
      const currentReadsStore = useCurrentReadsStore()
      const { t } = useStrings()
      const positiveDotRef = useTemplateRef('positiveDot')
      const negativeDotRef = useTemplateRef('negativeDot')
      return{
        t,
        currentReadsStore,
        positiveDotRef,
        negativeDotRef,
      }
    },
    data: () => {
      return {
        /**
         * @return Number
         *
         * An integer between -7 and 7 indicating the dots to be lit up.
         * A value * of -3 means the last 3 dots will be blue.
         * 0 means the first dot will be yellow and the last will be blue.
         * 5 means the first 5 dots will be yellow.
         **/
        displayedDotIndex: 0,
        animatedBalance: 0,
      }
    },
    mounted() {
      this.animatedBalance = this.balance
      this.displayedDotIndex = this.dotIndex
      window.addEventListener('resize', this.onResize)
      this.currentReadsStore.dotPositionsUpdated(this.getDotsCenterCoordindates())
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.onResize)
    },
    computed: {
      wrapperClasses: function () {
        let classes = ['dots']

        if (this.displayedDotIndex > 0) {
          classes.push('dots--positive')
        } else if (this.displayedDotIndex < 0) {
          classes.push('dots--negative')
        }

        return classes.join(' ')
      },

      dotIndex: function () {
        return this.currentReadsStore.dotIndex
      },
      dotCoordinates: function () {
        return this.currentReadsStore.dotCoordinates
      },
      title: function () {
        return this.t('home.balance')
      },
      balanceClasses: function () {
        let classes = ['dots__balance']
        if (this.currentReadsStore.balance < 0) {
          classes.push('dots__balance--negative')
        }
        return classes.join(' ')
      },
      balance: function () {
        return Math.abs(this.currentReadsStore.balance)
      },
    },
    methods: {
      onResize() {
        this.currentReadsStore.dotPositionsUpdated(this.getDotsCenterCoordindates())
      },
      getDotsCenterCoordindates() {
        const top = document.body.scrollTop
        const left = document.body.scrollLeft
        const positiveRect = this.positiveDotRef?.getBoundingClientRect() ?? DOM_RECT_EMPTY
        const negativeRect = this.negativeDotRef?.getBoundingClientRect() ?? DOM_RECT_EMPTY

        return {
          positiveY: Math.round(
            top + positiveRect.top + (positiveRect.height / 2)
          ),
          positiveX: Math.round(
            left + positiveRect.left + (positiveRect.width / 2)
          ),
          negativeY: Math.round(
            top + negativeRect.top + (negativeRect.height / 2)
          ),
          negativeX: Math.round(
            left + negativeRect.left + (negativeRect.width / 2)
          ),
        }
      },
      dotClasses(dotIndex) {
        let classes = ['dot', 'dots__dot-' + dotIndex]
        if (this.displayedDotIndex > 0 && dotIndex <= this.displayedDotIndex) {
          // Positive values.
          classes.push('dot--lit-positive')
        } else if (
          this.displayedDotIndex < 0 &&
          dotIndex >= this.displayedDotIndex + 8
        ) {
          // Negative values.
          classes.push('dot--lit-negative')
        }
        // For a zero balance / dotIndex we light up the first and last dots.
        else if (this.displayedDotIndex === 0 && dotIndex === 1) {
          classes.push('dot--lit-positive')
        } else if (this.displayedDotIndex === 0 && dotIndex === 7) {
          classes.push('dot--lit-negative')
        }

        return classes.join(' ')
      },
      /**
       * Visualizes + animates consumption changes.
       *
       * @param index See comment at this.currentDotIndex.
       */
      updateDots(index) {
        const delta = index - this.displayedDotIndex

        if (delta === 0) {
          return
        }

        const stepDuration = this.currentReadsStore.updateDotsStepDuration

        for (let i = 0; i < Math.abs(delta); i++) {
          setTimeout(() => {
            if (delta > 0) {
              this.displayedDotIndex++
            } else {
              this.displayedDotIndex--
            }
          }, stepDuration * Math.abs(i))
        }
      },
    },
    watch: {
      dotIndex: function (newValue) {
        this.updateDots(newValue)
      },
      balance: function (newValue) {
        TweenLite.to(this.$data, WATTAGE_TWEEN_DURATION, {
          animatedBalance: newValue,
        })
      },
    },
  }
</script>

<style lang="scss">
  $dot-font-size: 64px;
  $dot-border-color: $grey-light;
  $dot-border-width: 2px;
  $dot-border-radius: 100%;
  $dot-transition: all 0.4s ease-in-out 0s;

  $dot-size-min: 3.4rem;
  $dot-size-xs: 4.6rem;
  $dot-size-sm: 6rem;
  $dot-size-lg: 6.8rem;

  $dot-distance-min: 4.8rem;
  $dot-distance-xs: 6.8rem;
  $dot-distance-sm: 8.5rem;
  $dot-distance-lg: 10rem;

  $dots-center-from-top: 55%;

  .dot {
    transition: $dot-transition;
    border-radius: $dot-border-radius;
    font-size: $dot-font-size;
    width: $dot-size-min;
    height: $dot-size-min;
    line-height: $dot-size-min;
    text-align: center;
    color: $white;
    position: absolute;
    top: $dots-center-from-top;
    left: 50%;
    opacity: 0.8;
    border: $dot-border-width solid $dot-border-color;

    @media (min-height: 600px) and (min-width: 360px) {
      width: $dot-size-xs;
      height: $dot-size-xs;
      line-height: $dot-size-xs;
    }

    @media (min-height: 800px) and (min-width: $bp-sm) {
      width: $dot-size-sm;
      height: $dot-size-sm;
      line-height: $dot-size-sm;
    }

    @media (min-height: 1000px) and (min-width: $bp-lg) {
      width: $dot-size-lg;
      height: $dot-size-lg;
      line-height: $dot-size-lg;
    }

    &--lit-positive {
      border-color: $color-positive;
      background-color: $color-positive;
    }

    &--lit-negative {
      border-color: var(--color-negative-value);
      background-color: var(--color-negative-value);
    }
  }

  // The height of the dots container. Necessary since the dots are positioned
  // absolutely.
  $dots-height-min: 190px;
  $dots-height-xs: 41vh;
  $dots-height-sm: 350px;
  $dots-height-lg: 50vh;

  .dots {
    position: relative;
    min-height: $dots-height-min;
    transition: $transition-base;
    margin: 1vh 0;

    @media (min-height: 600px) and (min-width: 360px) {
      min-height: $dots-height-xs;
    }

    @media (min-height: 700px) and (min-width: $bp-sm) {
      margin-top: 0;
      margin-bottom: 0;
    }

    @media (min-height: 800px) and (min-width: $bp-sm) {
      min-height: $dots-height-sm;
    }

    @media (min-height: 1000px) and (min-width: $bp-sm) {
      margin-top: 3vh;
      margin-bottom: 2vh;
    }

    @for $i from 1 through 7 {
      &__dot-#{$i} {
        @include position-dot($i, $dot-distance-min);

        @media (min-height: 600px) and (min-width: 360px) {
          @include position-dot($i, $dot-distance-xs);
        }

        @media (min-height: 800px) and (min-width: $bp-sm) {
          @include position-dot($i, $dot-distance-sm);
        }

        @media (min-height: 1000px) and (min-width: $bp-lg) {
          @include position-dot($i, $dot-distance-lg);
        }
      }
    }

    $dot-sign-width: 0.1em;
    $dot-sign-size: 50%;

    &__plus,
    &__minus {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      width: 100%;
      height: 100%;
      opacity: 0;
    }

    &__plus {
      .dot--lit-positive & {
        opacity: 1;
      }

      &::before,
      &::after {
        position: absolute;
        content: '';
        display: block;
        background: $white;
      }

      &::before {
        width: $dot-sign-size;
        height: $dot-sign-width;
        left: math.div(100% - $dot-sign-size, 2);
        top: 50%;
        transform: translateY(-50%);
      }

      &::after {
        width: $dot-sign-width;
        height: $dot-sign-size;
        left: 50%;
        top: math.div(100% - $dot-sign-size, 2);
        transform: translateX(-50%);
      }
    }

    &__minus {
      .dot--lit-negative & {
        opacity: 1;
      }

      &::after {
        position: absolute;
        content: '';
        display: block;
        background: $white;
        width: $dot-sign-size;
        height: $dot-sign-width;
        top: 50%;
        left: math.div(100% - $dot-sign-size, 2);
        transform: translateY(-50%);
      }
    }

    &__balance {
      position: absolute;
      left: 50%;
      // Magic number to visually correct the positioning.
      top: $dots-center-from-top - 3%;
      transform: translate(-50%, -50%);
      text-align: center;
      transition: $transition-base;
      color: var(--color-positive-value);

      &--negative {
        // color: $supplier;
        color: var(--color-negative-value);
      }

      &-title {
        line-height: $line-height-sm;
      }
    }
  }
</style>
